/**
 * ReportGenerator.js — Sales-demo report generator (isolated MainAdmin tab)
 *
 * PURPOSE: Let sales type a prospect's company + rough hierarchy and instantly
 * produce a stunning, TeamGrid-branded PDF that shows "this is the insight
 * report you get if you subscribe."
 *
 * ZERO server load — fully client-side. No backend route, no DB, no AI.
 * Synthetic (fabricated but internally-consistent) data via reportEngine.js;
 * premium HTML via reportTemplates.js; PDF via the browser's print dialog.
 *
 * UX: guided 3-step form (Company → Team → Generate), theme-aware surface
 * (works in the panel's light OR dark mode), live side preview + full-screen
 * preview modal, one-click PDF / HTML export.
 */
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Select, ConfigProvider, theme as antdTheme } from 'antd';
import useApi from '../../hooks/useApi';
import { INDUSTRIES, INDUSTRY_KEYS, generateReport, orgChartFor, detectIndustry } from './reportgen/reportEngine';
import { TEMPLATES, renderReport } from './reportgen/reportTemplates';

const uid = () => Math.random().toString(36).slice(2, 9);

const DEFAULTS = { company: 'Meridian Consulting', industryKey: 'software', period: 'June 2026', workingDays: 22 };

const emptyDept = () => ({ id: uid(), name: '', people: [{ id: uid(), name: '', role: '' }] });

export default function ReportGenerator({ isDark }) {
  const api = useApi();
  const [company, setCompany] = useState(DEFAULTS.company);
  const [industryKey, setIndustryKey] = useState(DEFAULTS.industryKey);
  const [period, setPeriod] = useState(DEFAULTS.period);
  const [workingDays, setWorkingDays] = useState(DEFAULTS.workingDays);
  const [templateKey, setTemplateKey] = useState(TEMPLATES[0].key);
  // Hierarchy starts from the industry's default org chart.
  const [departments, setDepartments] = useState(() => orgChartFor(DEFAULTS.industryKey, uid));
  const [logoSvg, setLogoSvg] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const iframeRef = useRef(null);
  const fsIframeRef = useRef(null);

  // "Pristine" = hierarchy is still an auto-filled template (rep hasn't hand-edited it).
  // While pristine, switching industry re-fills the org chart. Once the rep edits
  // the team manually, we stop auto-replacing so their work is never wiped.
  const [pristine, setPristine] = useState(true);
  // Wrap the hierarchy setters used by manual edits so they mark the tree as touched.
  const editDepartments = useCallback((updater) => {
    setPristine(false);
    setDepartments(updater);
  }, []);

  // Whether the rep has manually chosen an industry. Until they do, we auto-detect
  // it from the company name they type. Once they pick one, we stop auto-changing it.
  const [industryManual, setIndustryManual] = useState(false);
  const [autoDetected, setAutoDetected] = useState(false); // for a small "auto" hint

  // Apply an industry key: set it, and re-fill the org chart if the team is untouched.
  const applyIndustry = useCallback((key) => {
    setIndustryKey(key);
    setPristine((wasPristine) => {
      if (wasPristine) setDepartments(orgChartFor(key, uid));
      return wasPristine;
    });
  }, []);

  // Rep picks industry from the dropdown → lock it (manual).
  const onIndustryChange = useCallback((key) => {
    setIndustryManual(true);
    setAutoDetected(false);
    applyIndustry(key);
  }, [applyIndustry]);

  // Rep types/changes the company name → auto-detect industry (unless they picked one).
  const onCompanyChange = useCallback((name) => {
    setCompany(name);
    if (industryManual) return;
    const guess = detectIndustry(name);
    if (guess) {
      setAutoDetected(true);
      applyIndustry(guess);
    }
  }, [industryManual, applyIndustry]);

  // Load the white TeamGrid logo once so downloaded HTML is self-contained.
  useEffect(() => {
    fetch('/logo-teamgrid.svg')
      .then((r) => (r.ok ? r.text() : ''))
      .then((svg) => setLogoSvg(svg ? svg.replace('<svg', '<svg class="logoimg"') : ''))
      .catch(() => setLogoSvg(''));
  }, []);

  const peopleCount = useMemo(
    () => departments.reduce((s, d) => s + d.people.filter((p) => p.name.trim()).length, 0),
    [departments]
  );

  const model = useMemo(() => {
    if (!company.trim() || peopleCount === 0) return null;
    return generateReport({
      company,
      industryKey,
      period,
      workingDays: Number(workingDays) || 22,
      departments: departments.map((d) => ({
        name: d.name.trim() || 'Team',
        people: d.people.filter((p) => p.name.trim()),
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, industryKey, period, workingDays, departments, peopleCount]);

  const html = useMemo(
    () => (model ? renderReport(templateKey, model, logoSvg) : ''),
    [model, templateKey, logoSvg]
  );

  const emptyDoc = '<body style="font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#94a3b8;text-align:center;padding:40px"><div><div style="font-size:15px;font-weight:600;color:#64748b">Nothing to preview yet</div><div style="font-size:13px;margin-top:6px">Add a company name and at least one person.</div></div></body>';

  // Push HTML into whichever iframe(s) are mounted.
  const writeInto = useCallback((ifr) => {
    if (!ifr) return;
    const doc = ifr.contentDocument || ifr.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html || emptyDoc);
    doc.close();
  }, [html]);

  useEffect(() => { writeInto(iframeRef.current); }, [writeInto]);
  useEffect(() => { if (fullscreen) writeInto(fsIframeRef.current); }, [fullscreen, writeInto]);

  // ─── Hierarchy editing (all manual edits mark the tree as touched) ─────────
  const addDept = () => editDepartments((d) => [...d, emptyDept()]);
  const removeDept = (id) => editDepartments((d) => d.filter((x) => x.id !== id));
  const setDeptName = (id, name) => editDepartments((d) => d.map((x) => (x.id === id ? { ...x, name } : x)));
  const addPerson = (did) => editDepartments((d) => d.map((x) => (x.id === did ? { ...x, people: [...x.people, { id: uid(), name: '', role: '' }] } : x)));
  const removePerson = (did, pid) => editDepartments((d) => d.map((x) => (x.id === did ? { ...x, people: x.people.filter((p) => p.id !== pid) } : x)));
  const setPerson = (did, pid, field, val) =>
    editDepartments((d) => d.map((x) => (x.id === did ? {
      ...x,
      people: x.people.map((p) => {
        if (p.id !== pid) return p;
        const next = { ...p, [field]: val };
        // If the role is edited, drop the preset tier so the engine re-infers it from the new title.
        if (field === 'role') delete next.tier;
        return next;
      }),
    } : x)));

  // Reset to the current industry's default org chart (pristine again).
  const loadSample = () => {
    setCompany(DEFAULTS.company); setPeriod(DEFAULTS.period); setWorkingDays(DEFAULTS.workingDays);
    setIndustryKey(DEFAULTS.industryKey);
    setDepartments(orgChartFor(DEFAULTS.industryKey, uid));
    setPristine(true); setIndustryManual(false); setAutoDetected(false);
  };
  const clearAll = () => {
    setCompany(''); setDepartments([emptyDept()]);
    setPristine(false); setIndustryManual(false); setAutoDetected(false);
  };

  const bulkPaste = () => {
    const text = window.prompt(
      'Paste hierarchy — one line per person as:\n\n  Department | Full Name | Role\n\n(Role optional). Example:\nEngineering | Arjun Mehta | Lead\nEngineering | Priya Nair | Developer\nSales | Neha Sharma | Account Manager'
    );
    if (!text) return;
    const map = {};
    text.split('\n').forEach((line) => {
      const parts = line.split(/[|,\t]/).map((s) => s.trim());
      if (!parts[0] || !parts[1]) return;
      const [dept, name, role = ''] = parts;
      (map[dept] = map[dept] || []).push({ id: uid(), name, role });
    });
    const next = Object.entries(map).map(([name, people]) => ({ id: uid(), name, people }));
    if (next.length) { setPristine(false); setDepartments(next); }
  };

  // ─── Apify enrichment (auto-fill team from website / LinkedIn) ──────────────
  // Paste a company website and/or LinkedIn company URL → backend runs Apify →
  // we review the scraped people in a checklist → import ticked people into the
  // team hierarchy. The Apify key stays server-side.
  const [siteUrl, setSiteUrl] = useState('');
  const [liUrl, setLiUrl] = useState('');
  const [enrichState, setEnrichState] = useState('idle'); // idle | running | ready | error
  const [enrichError, setEnrichError] = useState('');
  const [scraped, setScraped] = useState(null); // { company, people }
  const [picked, setPicked] = useState({}); // index -> bool
  const [reviewOpen, setReviewOpen] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);
  useEffect(() => () => stopPolling(), [stopPolling]);

  const startEnrich = useCallback(async () => {
    const website = siteUrl.trim();
    const linkedinUrl = liUrl.trim();
    if (!website && !linkedinUrl) { setEnrichError('Enter a website or LinkedIn company URL.'); setEnrichState('error'); return; }
    if (!linkedinUrl) {
      // No LinkedIn URL → we can only enrich company details, not the people
      // hierarchy. Warn but proceed (company info still auto-fills).
      // (User can paste the LinkedIn company URL for the full hierarchy.)
    }
    setEnrichError(''); setScraped(null); setEnrichState('running');
    try {
      const { data } = await api.post('/api/v1/admin/prospect-enrich/start', { website, linkedinUrl });
      if (!data?.success || !data.runId) throw new Error(data?.message || 'Could not start enrichment.');
      const runId = data.runId;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/api/v1/admin/prospect-enrich/status/${encodeURIComponent(runId)}`);
          const d = res.data || {};
          if (d.status === 'READY') {
            stopPolling();
            const result = d.result || { company: {}, people: [] };
            setScraped(result);
            setPicked(Object.fromEntries((result.people || []).map((_, i) => [i, true])));
            setEnrichState('ready');
            setReviewOpen(true);
          } else if (d.status === 'FAILED') {
            stopPolling();
            setEnrichError(d.message || 'Enrichment failed.');
            setEnrichState('error');
          }
          // else RUNNING → keep polling
        } catch (e) {
          stopPolling();
          setEnrichError(e?.response?.data?.message || e.message || 'Status check failed.');
          setEnrichState('error');
        }
      }, 4000);
    } catch (e) {
      setEnrichError(e?.response?.data?.message || e.message || 'Could not start enrichment.');
      setEnrichState('error');
    }
  }, [siteUrl, liUrl, api, stopPolling]);

  // Apply the ticked people into the team hierarchy, grouped by department.
  const applyScraped = useCallback(() => {
    if (!scraped) return;
    const chosen = (scraped.people || []).filter((_, i) => picked[i]);
    if (chosen.length) {
      const map = {};
      chosen.forEach((p) => {
        const dept = p.department || 'Team';
        (map[dept] = map[dept] || []).push({ id: uid(), name: p.name, role: p.role || '' });
      });
      const next = Object.entries(map).map(([name, people]) => ({ id: uid(), name, people }));
      setPristine(false);
      setDepartments(next);
    }
    // Auto-fill company details from the scrape where present.
    const c = scraped.company || {};
    if (c.name) setCompany(c.name);
    if (c.industry) {
      const match = INDUSTRY_KEYS.find((k) => INDUSTRIES[k].label.toLowerCase().includes(String(c.industry).toLowerCase().split(/[\s/]+/)[0]));
      if (match) setIndustryKey(match);
    }
    setReviewOpen(false);
    setEnrichState('idle');
  }, [scraped, picked]);

  const scrapedCount = scraped?.people?.length || 0;
  const pickedCount = Object.values(picked).filter(Boolean).length;

  // ─── Output actions ────────────────────────────────────────────────────────
  const openPrint = useCallback(() => {
    if (!html) return;
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to generate the PDF.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => setTimeout(() => w.print(), 600);
  }, [html]);

  const downloadHtml = useCallback(() => {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${company.replace(/[^a-z0-9]+/gi, '_')}_TeamGrid_Report.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [html, company]);

  // ─── Theme-aware tokens (self-contained; works in light OR dark panel) ─────
  const t = isDark
    ? {
        surface: 'bg-white/[0.03] border-white/10',
        surfaceSolid: 'bg-[#0d1420] border-white/10',
        nested: 'bg-black/20 border-white/10',
        text: 'text-white', sub: 'text-gray-400', faint: 'text-gray-500',
        input: 'bg-black/30 border-white/15 text-white placeholder-gray-500 focus:border-amber-400/60 focus:ring-amber-400/20',
        ghost: 'bg-white/5 border-white/10 text-gray-200 hover:bg-white/10',
        chipBtn: 'text-red-300/70 hover:text-red-300 hover:bg-red-500/10',
        stepDot: 'bg-white/10 text-gray-300', stepDotActive: 'bg-amber-500 text-black',
      }
    : {
        surface: 'bg-white border-gray-200 shadow-sm',
        surfaceSolid: 'bg-white border-gray-200 shadow-sm',
        nested: 'bg-gray-50 border-gray-200',
        text: 'text-gray-900', sub: 'text-gray-500', faint: 'text-gray-400',
        input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-amber-500 focus:ring-amber-500/20',
        ghost: 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
        chipBtn: 'text-gray-400 hover:text-red-500 hover:bg-red-50',
        stepDot: 'bg-gray-200 text-gray-600', stepDotActive: 'bg-amber-500 text-white',
      };

  const inputCls = `w-full rounded-lg px-3 py-2.5 text-sm border transition focus:outline-none focus:ring-2 ${t.input}`;
  const labelCls = `text-[11px] font-semibold uppercase tracking-wider ${t.faint} mb-1.5 block`;

  const StepBadge = ({ n, done }) => (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${done ? t.stepDotActive : t.stepDot}`}>
      {done ? '✓' : n}
    </span>
  );

  const canGenerate = !!model;

  // AntD theme for the dropdowns — amber accent to match the report brand.
  const antdConfig = {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: { colorPrimary: '#c5920a', borderRadius: 8, controlHeight: 40 },
  };
  const industryOptions = INDUSTRY_KEYS.map((k) => ({ value: k, label: INDUSTRIES[k].label }));
  const templateOptions = TEMPLATES.map((tp) => ({ value: tp.key, label: tp.label }));

  return (
    <div className={t.text}>
      {/* ── Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Report Generator</h1>
          <p className={`text-sm ${t.sub} mt-1 max-w-2xl`}>
            Type a prospect's company and rough team → generate a polished, TeamGrid-branded intelligence report to show
            them what they'll get. Fully offline · nothing leaves the browser.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadSample} className={`px-3 py-2 text-sm rounded-lg border ${t.ghost}`}>Load sample</button>
          <button onClick={clearAll} className={`px-3 py-2 text-sm rounded-lg border ${t.ghost}`}>Clear</button>
        </div>
      </div>

      {/* ── Top action bar: template + export (always visible, no scroll needed) ── */}
      <div className={`rounded-2xl border ${t.surface} p-3.5 mb-6 flex flex-wrap items-center gap-3`}>
        <div className="flex items-center gap-2.5">
          <span className={`text-[11px] font-semibold uppercase tracking-wider ${t.faint}`}>Template</span>
          <ConfigProvider theme={antdConfig}>
            <Select value={templateKey} onChange={setTemplateKey} options={templateOptions} style={{ minWidth: 210 }} />
          </ConfigProvider>
          <span className={`text-xs ${t.faint} hidden lg:inline max-w-[280px] truncate`}>{TEMPLATES.find((x) => x.key === templateKey)?.desc}</span>
        </div>
        <div className="flex items-center gap-2.5 ml-auto">
          {!canGenerate && <span className={`text-xs ${t.faint} hidden sm:inline`}>Add a company & a person to enable</span>}
          <button onClick={() => setFullscreen(true)} disabled={!canGenerate} className={`px-3.5 py-2.5 rounded-xl border text-sm font-medium ${t.ghost} disabled:opacity-40`}>Full preview</button>
          <button onClick={downloadHtml} disabled={!canGenerate} className={`px-3.5 py-2.5 rounded-xl border text-sm font-medium ${t.ghost} disabled:opacity-40`}>Download HTML</button>
          <button
            onClick={openPrint}
            disabled={!canGenerate}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold text-sm shadow-sm disabled:opacity-40 disabled:cursor-not-allowed hover:from-amber-400 hover:to-amber-500 transition flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
            Generate PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,440px)_1fr] gap-6 items-start">
        {/* ── LEFT: guided form ── */}
        <div className="space-y-5">
          {/* Step 0 — Auto-fill from website / LinkedIn (Apify) */}
          <div className={`rounded-2xl border ${t.surface} p-5`}>
            <div className="flex items-center gap-2.5 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/15 text-amber-500 shrink-0">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </span>
              <h3 className="font-semibold text-[15px]">Auto-fill from website</h3>
            </div>
            <p className={`text-[11px] ${t.faint} mb-3 pl-8`}>
              Paste the prospect's site and LinkedIn company page — we fetch the company details and employee hierarchy for you.
            </p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Company website</label>
                <input className={inputCls} value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://acme.com" />
              </div>
              <div>
                <label className={labelCls}>LinkedIn company URL <span className="normal-case text-gray-400 font-normal">— needed for the people hierarchy</span></label>
                <input className={inputCls} value={liUrl} onChange={(e) => setLiUrl(e.target.value)} placeholder="https://www.linkedin.com/company/acme" />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={startEnrich}
                  disabled={enrichState === 'running'}
                  className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:from-amber-400 hover:to-amber-500 transition flex items-center gap-2"
                >
                  {enrichState === 'running' ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" /></svg>
                      Fetching… (30s–2 min)
                    </>
                  ) : 'Fetch details'}
                </button>
                {enrichState === 'ready' && scraped && !reviewOpen && (
                  <button onClick={() => setReviewOpen(true)} className="text-xs font-medium text-amber-500 hover:text-amber-600">
                    Review {scrapedCount} {scrapedCount === 1 ? 'person' : 'people'} →
                  </button>
                )}
              </div>
              {enrichState === 'error' && enrichError && (
                <p className="text-xs text-red-500">{enrichError}</p>
              )}
            </div>
          </div>

          {/* Step 1 — Company */}
          <div className={`rounded-2xl border ${t.surface} p-5`}>
            <div className="flex items-center gap-2.5 mb-4">
              <StepBadge n={1} done={!!company.trim()} />
              <h3 className="font-semibold text-[15px]">Company details</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Company / Organisation</label>
                <input className={inputCls} value={company} onChange={(e) => onCompanyChange(e.target.value)} placeholder="e.g. Meridian Consulting" />
              </div>
              <div>
                <label className={labelCls}>
                  Industry
                  {autoDetected
                    ? <span className="normal-case font-normal ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 tracking-normal">auto-detected · editable</span>
                    : <span className="normal-case text-gray-400 font-normal"> — auto-detected from the company name</span>}
                </label>
                <ConfigProvider theme={antdConfig}>
                  <Select
                    value={industryKey}
                    onChange={onIndustryChange}
                    options={industryOptions}
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    placeholder="Search or pick an industry"
                  />
                </ConfigProvider>
                <p className={`text-[11px] ${t.faint} mt-1.5`}>
                  {pristine
                    ? 'Type the company name and we pick the industry + load a matching org chart below.'
                    : 'Your custom team is kept. Use “Load sample” to reset to a template.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Reporting period</label>
                  <input className={inputCls} value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="June 2026" />
                </div>
                <div>
                  <label className={labelCls}>Working days</label>
                  <input type="number" min="1" max="31" className={inputCls} value={workingDays} onChange={(e) => setWorkingDays(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 — Team */}
          <div className={`rounded-2xl border ${t.surface} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <StepBadge n={2} done={peopleCount > 0} />
                <h3 className="font-semibold text-[15px]">Team hierarchy</h3>
                <span className={`text-xs ${t.faint}`}>· {peopleCount} {peopleCount === 1 ? 'person' : 'people'}</span>
              </div>
              <button onClick={bulkPaste} className={`px-2.5 py-1.5 text-xs rounded-lg border ${t.ghost}`}>Bulk paste</button>
            </div>

            <div className="space-y-3">
              {departments.map((d, di) => (
                <div key={d.id} className={`rounded-xl border ${t.nested} p-3`}>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className={`text-[11px] font-bold ${t.faint} w-5 text-center shrink-0`}>{di + 1}</span>
                    <input className={`${inputCls} font-semibold`} value={d.name} onChange={(e) => setDeptName(d.id, e.target.value)} placeholder="Department (e.g. Engineering)" />
                    <button onClick={() => removeDept(d.id)} title="Remove department" className={`shrink-0 w-9 h-9 rounded-lg border ${t.ghost} ${t.chipBtn} flex items-center justify-center`}>✕</button>
                  </div>
                  <div className="space-y-2 pl-7">
                    {d.people.map((p) => (
                      <div key={p.id} className="flex items-center gap-2">
                        <input className={`${inputCls} flex-1`} value={p.name} onChange={(e) => setPerson(d.id, p.id, 'name', e.target.value)} placeholder="Full name" />
                        <input className={`${inputCls} w-[42%]`} value={p.role} onChange={(e) => setPerson(d.id, p.id, 'role', e.target.value)} placeholder="Role (optional)" />
                        <button onClick={() => removePerson(d.id, p.id)} title="Remove person" className={`shrink-0 w-9 h-9 rounded-lg border ${t.ghost} ${t.chipBtn} flex items-center justify-center`}>✕</button>
                      </div>
                    ))}
                    <button onClick={() => addPerson(d.id)} className="text-xs font-medium text-amber-500 hover:text-amber-600 mt-1">+ Add person</button>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={addDept} className={`mt-3 w-full py-2.5 text-sm font-medium rounded-lg border border-dashed ${isDark ? 'border-white/15 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              + Add department
            </button>
          </div>
        </div>

        {/* ── RIGHT: sticky live preview ── */}
        <div className={`rounded-2xl border ${t.surface} p-3 xl:sticky xl:top-4`}>
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${t.faint}`}>Live preview</span>
              {model && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-medium">{model.kpis.people} people · {model.kpis.hours.toLocaleString('en-IN')} h</span>}
            </div>
            <button onClick={() => setFullscreen(true)} disabled={!canGenerate} className={`text-xs font-medium px-2.5 py-1 rounded-lg border ${t.ghost} disabled:opacity-40 flex items-center gap-1.5`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
              Expand
            </button>
          </div>
          <iframe
            ref={iframeRef}
            title="Report preview"
            className="w-full rounded-xl bg-white border border-black/10"
            style={{ height: 'calc(100vh - 140px)', minHeight: 560 }}
          />
        </div>
      </div>

      {/* ── Full-screen preview modal ── */}
      {fullscreen && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex flex-col" onClick={() => setFullscreen(false)}>
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ background: '#0d1420', borderColor: 'rgba(255,255,255,0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
              <span className="font-semibold truncate" style={{ color: '#ffffff' }}>{company || 'Report'} — Full Preview</span>
              {model && <span className="text-xs whitespace-nowrap" style={{ color: '#94a3b8' }}>{model.kpis.people} people · {model.kpis.hours.toLocaleString('en-IN')} h · {model.kpis.focus}% focus</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={openPrint} className="px-3.5 py-2 rounded-lg font-semibold text-sm" style={{ background: 'linear-gradient(90deg,#f59e0b,#d97706)', color: '#ffffff' }}>Generate PDF</button>
              <button onClick={() => setFullscreen(false)} className="px-3.5 py-2 rounded-lg text-sm border" style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.15)', color: '#ffffff' }}>Close ✕</button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden p-4 sm:p-8 flex justify-center" onClick={(e) => e.stopPropagation()}>
            <iframe
              ref={fsIframeRef}
              title="Report full preview"
              className="w-full max-w-[1080px] h-full rounded-xl bg-white shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* ── Review scraped people modal ── */}
      {reviewOpen && scraped && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReviewOpen(false)}>
          <div className={`w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border ${t.surfaceSolid}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
              <div>
                <h3 className="font-semibold text-[15px]">Review fetched team</h3>
                <p className={`text-xs ${t.sub} mt-0.5`}>
                  {scraped.company?.name ? `${scraped.company.name} · ` : ''}{scrapedCount} {scrapedCount === 1 ? 'person' : 'people'} found
                  {scraped.company?.industry ? ` · ${scraped.company.industry}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setPicked(Object.fromEntries((scraped.people || []).map((_, i) => [i, true])))} className={`text-xs px-2.5 py-1.5 rounded-lg border ${t.ghost}`}>All</button>
                <button onClick={() => setPicked({})} className={`text-xs px-2.5 py-1.5 rounded-lg border ${t.ghost}`}>None</button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {scrapedCount === 0 && (
                <p className={`text-sm ${t.sub} text-center py-8`}>
                  No people were returned. {scraped.company?.name ? 'Company details were still imported.' : 'Try adding the LinkedIn company URL.'}
                </p>
              )}
              {(scraped.people || []).map((p, i) => (
                <label key={i} className={`flex items-center gap-3 rounded-xl border ${t.nested} px-3 py-2.5 cursor-pointer`}>
                  <input type="checkbox" checked={!!picked[i]} onChange={(e) => setPicked((prev) => ({ ...prev, [i]: e.target.checked }))} className="w-4 h-4 accent-amber-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className={`text-xs ${t.sub} truncate`}>{p.role || '—'}{p.location ? ` · ${p.location}` : ''}</div>
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 shrink-0">{p.department || 'Team'}</span>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 p-4 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
              <span className={`text-xs ${t.faint}`}>{pickedCount} selected → grouped into departments</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setReviewOpen(false)} className={`px-3.5 py-2 rounded-lg text-sm border ${t.ghost}`}>Cancel</button>
                <button onClick={applyScraped} className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold text-sm hover:from-amber-400 hover:to-amber-500 transition">
                  Import to team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
